import {
  CdkDrag,
  type CdkDragDrop,
  CdkDropList,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { DatePipe } from '@angular/common';
import { Component, inject, type OnInit, signal } from '@angular/core';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Routes } from '@core/routes/routes';
import { ButtonModule } from '@fino-ui/button';
import { FinoCommonModule } from '@fino-ui/common';
import { IbanPipe } from '@fino-ui/finances';
import { SubmittableForm } from '@fino-ui/forms';
import { FinoIconModule } from '@fino-ui/icon';
import { FinoInputModule } from '@fino-ui/input';
import { FinoTranslationModule } from '@fino-ui/translation';
import { filter, map } from 'lodash';
import { PdfJsViewerModule } from 'ng2-pdfjs-viewer';
import { ClipboardService } from 'ngx-clipboard';
import { Subscription } from 'rxjs';
import { PdfViewerComponent } from '../../components/pdf-viewer/pdf-viewer.component';
import { IdentifyDocumentService } from '../identify-document/identify-document.service';
import { SiblingsService } from './siblings.service';

const SIBLINGS_TYPES = {
  DOCUMENT_TYPE_RELEVANT: 'relevant',
  DOCUMENT_TYPE_NOT_RELEVANT: 'notRelevant',
  DOCUMENT_TYPE_DUPLICAT: 'duplicat',
};

@Component({
  selector: 'app-siblings',
  templateUrl: './siblings.component.html',
  styleUrls: ['./siblings.component.sass'],
  imports: [
    PdfJsViewerModule,
    FinoTranslationModule,
    CdkDropList,
    CdkDrag,
    FinoCommonModule,
    FinoIconModule,
    FormsModule,
    ButtonModule,
    ReactiveFormsModule,
    FinoInputModule,
    IbanPipe,
    DatePipe,
    PdfViewerComponent,
  ],
})
export class SiblingsComponent implements OnInit {
  private readonly identifyDocumentService = inject(IdentifyDocumentService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly siblingsService = inject(SiblingsService);
  private readonly clipboardService = inject(ClipboardService);

  public SIBLINGS_TYPES = SIBLINGS_TYPES;

  public readonly siblings = signal<any[]>([]);
  public readonly originalDocument = signal<any>(null);
  public readonly originalPdfUrl = signal('');
  public readonly siblingPdfUrl = signal('');
  public readonly selectedJobId = signal('');
  public readonly previewUrl = signal('');
  public readonly showPreview = signal(false);
  public readonly documentsToMerge = signal<string[]>([]);
  public readonly documentsToDismiss = signal<string[]>([]);
  public readonly mergeButtonActive = signal(false);

  mergeSiblingForm = new SubmittableForm({
    documentsChecked: new FormControl(false, Validators.required),
  });

  mergeSubscription: Subscription = Subscription.EMPTY;

  drop(event: CdkDragDrop<any[]>) {
    this.siblings.update((siblings) => {
      moveItemInArray(siblings, event.previousIndex, event.currentIndex);
      return [...siblings];
    });
  }

  selectJob(job: any) {
    const { id } = job;
    if (id === this.selectedJobId()) return;
    this.selectedJobId.set(job.id);
    this.siblingPdfUrl.set(job.docUrl);
  }

  selectSiblingState($event: any) {
    $event.preventDefault();
  }

  createPreview() {
    this.documentsToMerge.set(
      map(
        filter(
          this.siblings(),
          (s) => s.mergeSiblingState === SIBLINGS_TYPES.DOCUMENT_TYPE_RELEVANT,
        ),
        (s) => s.id,
      ),
    );
    this.documentsToDismiss.set(
      map(
        filter(
          this.siblings(),
          (s) => s.mergeSiblingState === SIBLINGS_TYPES.DOCUMENT_TYPE_DUPLICAT,
        ),
        (s) => s.id,
      ),
    );

    this.mergeSubscription = this.siblingsService
      .createPreview(this.documentsToDismiss(), this.documentsToMerge())
      .subscribe((resp: any) => {
        this.showPreview.set(true);
        this.previewUrl.set(escape(resp.preview.links.seeOther));
      });
  }

  abortMerge() {
    this.router.navigate([Routes.IdentifyDocument, this.originalDocument().id]);
  }

  copyDocumentId(documentId: string, $event: any) {
    $event.preventDefault();
    $event.stopPropagation();
    this.clipboardService.copy(documentId);
  }

  abortPreview() {
    this.showPreview.set(false);
  }

  mergeDocuments() {
    this.mergeSubscription = this.siblingsService
      .merge(this.documentsToDismiss(), this.documentsToMerge())
      .subscribe((resp: any) => {
        this.router.navigate([Routes.IdentifyDocument, resp.id]);
      });
  }

  ngOnInit() {
    this.route.params.subscribe((r) => {
      this.mergeSiblingForm.controls.documentsChecked.valueChanges.subscribe(
        (change) => {
          this.mergeButtonActive.set(change);
        },
      );

      this.identifyDocumentService
        .fetchNextJob(r.id)
        .subscribe((originalDocument: any) => {
          this.originalPdfUrl.set(originalDocument.docUrl);
          originalDocument.isOriginal = true;
          this.originalDocument.set(originalDocument);

          this.identifyDocumentService
            .getSiblings(r.id)
            .subscribe((siblings) => {
              const allSiblings = [originalDocument, ...(siblings as [])];
              allSiblings.forEach((s) => {
                s.mergeSiblingState =
                  this.SIBLINGS_TYPES.DOCUMENT_TYPE_NOT_RELEVANT;
              });

              allSiblings[0].mergeSiblingState =
                this.SIBLINGS_TYPES.DOCUMENT_TYPE_RELEVANT;

              this.siblings.set(allSiblings);
              this.siblingPdfUrl.set(originalDocument.docUrl);
            });
        });
    });
  }
}
